import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useCreateTrip } from "@/lib/api/hooks";
import { useAuthStore } from "@/lib/store";

const previewTrips = [
  {
    title: "Summer in Italy",
    subtitle: "Rome, Amalfi, Florence, and Tuscany",
    query:
      "Plan a 2-week luxury Italy itinerary with art, food, and coastal villages",
    image:
      "https://images.unsplash.com/photo-1523906834658-6e24ef2386f9?w=900&q=80",
    tag: "14 days",
    size: "lg" as const,
  },
  {
    title: "Paris",
    subtitle: "Museums, café terraces, and river nights",
    query:
      "3-day Paris city trip with museums, food, and evening walks",
    image:
      "https://images.unsplash.com/photo-1502602898657-3e91760cbb34?w=900&q=80",
    tag: "city trip",
    size: "sm" as const,
  },
  {
    title: "London",
    subtitle: "Historic streets, markets, and skyline nights",
    query:
      "3-day London city trip with markets, museums, and central neighborhoods",
    image:
      "https://images.unsplash.com/photo-1513635269975-59663e0ac1ad?w=900&q=80",
    tag: "city trip",
    size: "sm" as const,
  },
  {
    title: "China",
    subtitle: "Shanghai and Beijing city highlights with food and culture",
    query:
      "Plan a China trip covering Shanghai and Beijing with skyline views, landmarks, and food",
    image:
      "https://images.unsplash.com/photo-1538428494232-9c0d8a3ab403?w=900&q=80",
    tag: "8 days",
    size: "lg" as const,
  },
];

const cityCards = [
  {
    title: "Paris",
    subtitle: "Art, cafés and quiet museum mornings",
    query: "3-day Paris city break with museums, cafes and evening river cruise",
  },
  {
    title: "Barcelona",
    subtitle: "Gaudi routes and seaside evenings",
    query: "4-day Barcelona trip with tapas, architecture and beach time",
  },
  {
    title: "Istanbul",
    subtitle: "Grand bazaars and Bosphorus sunsets",
    query: "3-day Istanbul itinerary with historic mosques and local food",
  },
  {
    title: "New York",
    subtitle: "Skyline nights and neighborhood food trails",
    query: "5-day New York trip with Broadway, food and modern art",
  },
];

const capabilities = [
  { label: "Flights" },
  { label: "Hotels" },
  { label: "Experiences" },
  { label: "Restaurants" },
  { label: "Maps" },
];

export default function HomePage() {
  const [query, setQuery] = useState("");
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const createTrip = useCreateTrip();
  const account = useAuthStore((s) => s.account);

  useEffect(() => {
    const section = searchParams.get("section");
    if (!section) return;

    const el = document.getElementById(section);
    if (el) {
      const offset = 120;
      const top = el.getBoundingClientRect().top + window.scrollY - offset;
      window.scrollTo({ top, behavior: "smooth" });
    }

    const url = new URL(window.location.href);
    url.searchParams.delete("section");
    window.history.replaceState({}, "", `${url.pathname}${url.search}${url.hash}`);
  }, [searchParams]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    if (!account && !localStorage.getItem("access_token")) {
      navigate(`/auth/login?next=${encodeURIComponent("/")}`);
      return;
    }
    try {
      const trip = await createTrip.mutateAsync({
        title: query.slice(0, 100),
        destination: "",
        query,
      });
      navigate(`/plan/${trip.id}`, { state: { initialPrompt: query } });
    } catch {
      navigate("/auth/login");
    }
  };

  return (
    <div className="px-3 pt-3 pb-8 md:px-4 md:pt-4 md:pb-12 lg:px-5">
      <div className="mx-auto max-w-[92rem] space-y-8">
        <section className="relative overflow-hidden rounded-[2rem] frost-panel px-6 pb-10 pt-12 md:px-10 md:pt-16">
          <div className="absolute -top-24 right-[-8%] h-72 w-72 rounded-full bg-white/25 blur-[90px]" />
          <div className="absolute -bottom-24 left-[-6%] h-72 w-72 rounded-full bg-accent/25 blur-[100px]" />

          <div className="relative z-10 grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-center">
            <div>
              <p className="inline-flex items-center rounded-full border border-white/25 bg-white/10 px-3 py-1 text-xs font-semibold text-ink-light">
                NEXT-GEN TRAVEL PLANNER
              </p>
              <h1 className="mt-5 text-5xl font-black leading-[0.98] text-ink sm:text-7xl lg:text-8xl">
                Travel
                <span className="block font-black bg-gradient-to-r from-white via-white to-accent bg-clip-text text-transparent">
                  differently.
                </span>
              </h1>
              <p className="mt-4 max-w-xl text-base text-ink-light sm:text-lg">
                Describe the trip once. Get flights, stays, maps, and day-by-day
                actions in one seamless AI cockpit.
              </p>

              <form onSubmit={handleSubmit} className="mt-7">
                <div className="flex flex-col gap-3 rounded-2xl frost-card p-2 sm:flex-row">
                  <input
                    type="text"
                    value={query}
                    onChange={(e) => setQuery(e.target.value)}
                    placeholder="Plan a 10-day trip across Japan with food focus"
                    className="w-full rounded-xl border border-transparent bg-transparent px-4 py-3 text-base font-semibold text-white placeholder:text-white/80 focus:border-white/20 focus:outline-none"
                    autoFocus
                  />
                  <button
                    type="submit"
                    disabled={createTrip.isPending}
                    className="rounded-xl bg-white px-5 py-3 text-sm font-bold text-deep transition hover:bg-white/90 disabled:opacity-60"
                  >
                    {createTrip.isPending ? "Creating..." : "Start planning"}
                  </button>
                </div>
              </form>

              <div className="mt-4 flex flex-wrap gap-2">
                {[
                  "Weekend in Paris",
                  "Thailand island hopping",
                  "Family trip to Japan",
                  "Italy food trail",
                ].map((prompt) => (
                  <button
                    key={prompt}
                    onClick={() => setQuery(prompt)}
                    className="rounded-full border border-white/25 bg-white/10 px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-white/20"
                  >
                    {prompt}
                  </button>
                ))}
              </div>

              <div className="mt-3 flex flex-wrap gap-2">
                {[
                  { label: "Budget", value: "Budget trip" },
                  { label: "Mid-range", value: "Mid-range budget" },
                  { label: "Luxury", value: "Luxury trip" },
                ].map((budget) => (
                  <button
                    key={budget.label}
                    onClick={() =>
                      setQuery((prev) =>
                        prev.trim()
                          ? `${prev.replace(/\s*[-|]\s*(budget trip|mid-range budget|luxury trip)$/i, "")} - ${budget.value}`
                          : budget.value
                      )
                    }
                    className="rounded-full border border-white/25 bg-white/10 px-3.5 py-1.5 text-xs font-semibold text-white transition hover:bg-white/20"
                  >
                    {budget.label}
                  </button>
                ))}
              </div>
            </div>

            <div className="relative space-y-3">
              <button
                onClick={() => setQuery(previewTrips[0].query)}
                className="group relative h-52 w-full overflow-hidden rounded-2xl text-left transition hover:-translate-y-0.5"
              >
                <img
                  src={previewTrips[0].image}
                  alt={previewTrips[0].title}
                  className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-deep via-deep/35 to-transparent" />
                <div className="absolute inset-0 p-4">
                  <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase text-deep">
                    {previewTrips[0].tag}
                  </span>
                  <h3 className="mt-3 text-3xl font-black text-white">{previewTrips[0].title}</h3>
                  <p className="mt-1 text-sm text-white/75">{previewTrips[0].subtitle}</p>
                </div>
              </button>

              <div className="grid grid-cols-2 gap-3">
                {previewTrips.slice(1, 3).map((trip) => (
                  <button
                    key={trip.title}
                    onClick={() => setQuery(trip.query)}
                    className="group relative h-36 w-full overflow-hidden rounded-2xl text-left transition hover:-translate-y-0.5"
                  >
                    <img
                      src={trip.image}
                      alt={trip.title}
                      className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-deep via-deep/35 to-transparent" />
                    <div className="absolute inset-0 p-4">
                      <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase text-deep">
                        {trip.tag}
                      </span>
                      <h3 className="mt-3 text-xl font-bold text-white">{trip.title}</h3>
                      <p className="mt-1 text-xs text-white/75">{trip.subtitle}</p>
                    </div>
                  </button>
                ))}
              </div>

              <button
                onClick={() => setQuery(previewTrips[3].query)}
                className="group relative h-40 w-full overflow-hidden rounded-2xl text-left transition hover:-translate-y-0.5"
              >
                <img
                  src={previewTrips[3].image}
                  alt={previewTrips[3].title}
                  className="absolute inset-0 h-full w-full object-cover transition duration-700 group-hover:scale-105"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-deep via-deep/35 to-transparent" />
                <div className="absolute inset-0 p-4">
                  <span className="rounded-full bg-white px-2 py-0.5 text-[10px] font-bold uppercase text-deep">
                    {previewTrips[3].tag}
                  </span>
                  <h3 className="mt-3 text-2xl font-black text-white">{previewTrips[3].title}</h3>
                  <p className="mt-1 text-sm text-white/75">{previewTrips[3].subtitle}</p>
                </div>
              </button>
            </div>
          </div>
        </section>

        <section
          id="explore"
          className="scroll-mt-28 grid gap-6 lg:grid-cols-[0.95fr_1.05fr]"
        >
          <div className="rounded-3xl frost-card p-6 md:p-8">
            <h2 className="text-2xl font-bold text-ink">Your unified travel stack</h2>
            <p className="mt-2 text-sm text-ink-light">
              Switch from scattered tabs to one interface with itinerary logic,
              live options, and instant edits.
            </p>
            <div className="mt-6 grid grid-cols-2 gap-3">
              {capabilities.map((item) => (
                <div
                  key={item.label}
                  className="rounded-xl border border-white/15 bg-white/5 px-3 py-3 text-sm font-semibold text-ink"
                >
                  {item.label}
                </div>
              ))}
            </div>
          </div>

          <div className="rounded-3xl frost-card p-6 md:p-8">
            <div className="flex items-center justify-between">
              <h2 className="text-2xl font-bold text-ink">City quick starts</h2>
              <span className="text-xs font-semibold text-accent">one click prompts</span>
            </div>
            <div className="mt-5 grid gap-3 sm:grid-cols-2">
              {cityCards.map((city) => (
                <button
                  key={city.title}
                  onClick={() => setQuery(city.query)}
                  className="rounded-2xl border border-white/15 bg-white/[0.08] p-4 text-left transition hover:border-white/35 hover:bg-white/[0.12]"
                >
                  <h3 className="text-lg font-bold text-ink">{city.title}</h3>
                  <p className="mt-1 text-sm text-ink-light">{city.subtitle}</p>
                </button>
              ))}
            </div>
          </div>
        </section>

        <section
          id="about"
          className="scroll-mt-28 rounded-3xl frost-panel px-6 py-10 text-center md:px-10"
        >
          <h2 className="text-3xl font-black text-ink sm:text-4xl">
            Launch your next journey now
          </h2>
          <p className="mx-auto mt-3 max-w-xl text-ink-light">
            Plan faster, compare smarter, and refine every day with AI in one
            futuristic workspace.
          </p>
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
            className="mt-7 rounded-xl bg-accent px-7 py-3 text-sm font-bold text-deep transition hover:bg-accent-light"
          >
            Open planner
          </button>
        </section>
      </div>
    </div>
  );
}
